import React, { useEffect, useRef } from 'react';
import Phaser from 'phaser';
import { HomeScene } from '../scenes/HomeScene';
import { TowerDefenseScene } from '../scenes/TowerDefenseScene';
import { ResultsScene } from '../scenes/ResultsScene';
import { LeaderboardScene } from '../scenes/LeaderboardScene';
import { BestiaryScene } from '../scenes/BestiaryScene';
import { EventScene } from '../scenes/EventScene';
import { AuthModal } from './AuthModal';
import { UpgradeModal } from './UpgradeModal';
import { GAME_CONFIG } from '../lib/game-config';
import { KINGSOMNI_GAME_ABI, KINGSOMNI_LEADERBOARD_ABI } from '../lib/contract-abi';
import { usePrivy, useWallets } from '@privy-io/react-auth';
import { ethers } from 'ethers';
import toast from 'react-hot-toast';
import { getPreferredEoaWallet } from '../utils/wallet';

interface GameComponentProps {
  user?: any;
}

export function GameComponent({ user }: GameComponentProps) {
  const gameRef = useRef<Phaser.Game | null>(null);
  const isSubmittingScoreRef = useRef(false);
  const isClaimingRewardRef = useRef(false);
  const [isReady, setIsReady] = React.useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = React.useState(false);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = React.useState(false);
  const homeSceneRef = useRef<HomeScene | null>(null);
  const { login } = usePrivy();
  const { wallets } = useWallets();

  useEffect(() => {
    // Set ready after a short delay to ensure DOM is ready
    const timer = setTimeout(() => setIsReady(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!isReady) return;

    // Create Phaser game config
    const config: Phaser.Types.Core.GameConfig = {
      type: Phaser.AUTO,
      width: GAME_CONFIG.CANVAS.WIDTH,
      height: GAME_CONFIG.CANVAS.HEIGHT,
      parent: 'phaser-game',
      backgroundColor: '#1a1a2e',
      physics: {
        default: 'arcade',
        arcade: {
          gravity: { x: 0, y: 0 },
          debug: false,
        },
      },
        scene: [HomeScene, EventScene, TowerDefenseScene, ResultsScene, LeaderboardScene, BestiaryScene],
      scale: {
        mode: Phaser.Scale.FIT,
        autoCenter: Phaser.Scale.CENTER_BOTH,
      },
    };

    // Create the game instance
    if (!gameRef.current) {
      gameRef.current = new Phaser.Game(config);
      
      // Store reference to HomeScene after a small delay to ensure scenes are ready
      setTimeout(() => {
        const homeScene = gameRef.current?.scene.getScene('HomeScene') as HomeScene;
        if (homeScene) {
          homeSceneRef.current = homeScene;
          // If we already have user data, update it immediately
          if (user) {
            homeScene.updateUser(user);
          }
          // Set up event listener for authentication modal
          homeScene.events.on('showAuthModal', () => {
            setIsAuthModalOpen(true);
          });
          // Set up event listener for upgrade modal
          homeScene.events.on('showUpgradeModal', () => {
            setIsUpgradeModalOpen(true);
          });
        }
        
        const handleExecuteSttClaim = async (data: any) => {
          if (isSubmittingScoreRef.current) {
            return;
          }
          isSubmittingScoreRef.current = true;

          const wallet = getPreferredEoaWallet(wallets as any[]);
          if (!wallet) {
            toast.error("Wallet not connected");
            if (data.onError) data.onError(new Error("Wallet not connected"));
            isSubmittingScoreRef.current = false;
            return;
          }

          try {
            const ethereumProvider = await wallet.getEthereumProvider();
            const provider = new ethers.BrowserProvider(ethereumProvider as any);
            const signer = await provider.getSigner();

            const connectedAddress = await signer.getAddress();
            const payload = {
              playerAddress: connectedAddress,
              scoreDelta: Number(data.scoreDelta ?? 0),
              killsDelta: Number(data.killsDelta ?? 0),
              durationMs: Number(data.durationMs ?? 0),
              seed: String(data.seed ?? `${connectedAddress}-${Date.now()}`),
              timestamp: Number(data.timestamp ?? Date.now()),
            };
            if (payload.scoreDelta <= 0 && payload.killsDelta <= 0) {
              throw new Error('No score to submit');
            }
            if (
              !GAME_CONFIG.CONTRACTS.LEADERBOARD ||
              GAME_CONFIG.CONTRACTS.LEADERBOARD === ethers.ZeroAddress
            ) {
              throw new Error('Leaderboard contract address is not configured');
            }

            toast.loading("Preparing signed score payload...", { id: "claim-tx" });
            const backendBaseUrl = GAME_CONFIG.API.BACKEND_URL.replace(/\/$/, '');
            const signatureResponse = await fetch(`${backendBaseUrl}/api/submit-score`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(payload),
            });

            const signatureJson = await signatureResponse.json().catch(() => ({}));
            if (!signatureResponse.ok || !signatureJson?.success) {
              throw new Error(signatureJson?.error || 'Failed to get signature from backend');
            }

            const signedRequest = signatureJson.request;
            const signature = signatureJson.signature as string;
            if (!signedRequest || !signature) {
              throw new Error('Invalid signature response from backend');
            }
            if (String(signedRequest.player).toLowerCase() !== connectedAddress.toLowerCase()) {
              throw new Error('Signed payload player address mismatch');
            }

            const contract = new ethers.Contract(
              GAME_CONFIG.CONTRACTS.LEADERBOARD,
              KINGSOMNI_LEADERBOARD_ABI,
              signer,
            );

            toast.loading("Sign transaction to submit score...", { id: "claim-tx" });
            const tx = await contract.submitScore(
              {
                player: signedRequest.player,
                scoreDelta: BigInt(signedRequest.scoreDelta),
                killsDelta: BigInt(signedRequest.killsDelta),
                matchId: signedRequest.matchId,
                deadline: BigInt(signedRequest.deadline),
              },
              signature,
            );

            toast.loading("Transaction pending...", { id: "claim-tx" });
            await tx.wait();

            toast.success("Score submitted on-chain!", { id: "claim-tx" });
            if (data.onSuccess) data.onSuccess();
          } catch (err: any) {
            console.error("Exec STT Error:", err);
            toast.error("Failed to submit score on-chain.", { id: "claim-tx" });
            if (data.onError) data.onError(err);
          } finally {
            isSubmittingScoreRef.current = false;
          }
        };

        const handleExecuteRewardClaim = async (data: any) => {
          if (isClaimingRewardRef.current) {
            return;
          }
          isClaimingRewardRef.current = true;

          const wallet = getPreferredEoaWallet(wallets as any[]);
          if (!wallet) {
            toast.error("Wallet not connected");
            if (data.onError) data.onError(new Error("Wallet not connected"));
            isClaimingRewardRef.current = false;
            return;
          }

          try {
            const ethereumProvider = await wallet.getEthereumProvider();
            const provider = new ethers.BrowserProvider(ethereumProvider as any);
            const signer = await provider.getSigner();
            const connectedAddress = await signer.getAddress();

            if (!GAME_CONFIG.CONTRACTS.GAME || GAME_CONFIG.CONTRACTS.GAME === ethers.ZeroAddress) {
              throw new Error('Game contract address is not configured');
            }

            const payload = {
              playerAddress: connectedAddress,
              scoreDelta: Number(data.scoreDelta ?? 0),
              killsDelta: Number(data.killsDelta ?? 0),
              durationMs: Number(data.durationMs ?? 0),
              seed: String(data.seed ?? `${connectedAddress}-${Date.now()}`),
              timestamp: Number(data.timestamp ?? Date.now()),
            };

            toast.loading("Calculating session reward...", { id: "claim-reward-tx" });
            const backendBaseUrl = GAME_CONFIG.API.BACKEND_URL.replace(/\/$/, '');
            const rewardResponse = await fetch(`${backendBaseUrl}/api/submit-score`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(payload),
            });
            const rewardJson = await rewardResponse.json().catch(() => ({}));
            if (!rewardResponse.ok || !rewardJson?.success) {
              throw new Error(rewardJson?.error || 'Failed to calculate reward');
            }

            const rewardAmountWei = BigInt(rewardJson.rewardAmountWei ?? "0");
            const rewardAmountStt = String(rewardJson.rewardAmountStt ?? "0");
            if (rewardAmountWei <= 0n) {
              throw new Error("No STT reward for this session");
            }
            const scoreDelta = Math.max(0, Number(data.scoreDelta ?? 0));
            const killsDelta = Math.max(0, Number(data.killsDelta ?? 0));

            const gameContract = new ethers.Contract(
              GAME_CONFIG.CONTRACTS.GAME,
              KINGSOMNI_GAME_ABI,
              signer,
            );

            toast.loading("Sign transaction to claim reward...", { id: "claim-reward-tx" });
            const tx = await gameContract.claimSTTAndScore(
              BigInt(scoreDelta),
              BigInt(killsDelta),
              rewardAmountWei,
              0,
              "0x",
            );

            toast.loading("Reward claim pending...", { id: "claim-reward-tx" });
            await tx.wait();

            toast.success(`Reward claimed: ${rewardAmountStt} STT`, { id: "claim-reward-tx" });
            if (data.onSuccess) data.onSuccess({ rewardAmountStt, rewardAmountWei: rewardAmountWei.toString() });
          } catch (err: any) {
            console.error("Exec Reward Claim Error:", err);
            toast.error("Failed to claim STT reward.", { id: "claim-reward-tx" });
            if (data.onError) data.onError(err);
          } finally {
            isClaimingRewardRef.current = false;
          }
        };

        // Setup listeners for score submission from ResultsScene and in-game GameOver panel
        const resultsScene = gameRef.current?.scene.getScene('ResultsScene') as ResultsScene;
        if (resultsScene) {
          resultsScene.events.on('executeSTTClaim', handleExecuteSttClaim);
        }
        const gameScene = gameRef.current?.scene.getScene('GameScene') as TowerDefenseScene;
        if (gameScene) {
          gameScene.events.on('executeSTTClaim', handleExecuteSttClaim);
          gameScene.events.on('executeRewardClaim', handleExecuteRewardClaim);
        }
      }, 500);
    }

    // Cleanup on unmount
    return () => {
      if (gameRef.current) {
        gameRef.current.destroy(true);
        gameRef.current = null;
        homeSceneRef.current = null;
      }
    };
  }, [isReady, user]);
  
  // Update HomeScene when user data changes
  useEffect(() => {
    if (homeSceneRef.current && user) {
      homeSceneRef.current.updateUser(user);
    }
  }, [user]);
  
  // Handle modal actions
  const handleCloseModal = () => {
    setIsAuthModalOpen(false);
  };
  
  const handleSignIn = () => {
    login();
    setIsAuthModalOpen(false);
  };
  
  const handleRegisterUsername = () => {
    // TODO: Point to Kingsomni Profile creation or Somnia explorer
    console.log("Redirecting to profile registration...");
    setIsAuthModalOpen(false);
  };

  return (
    <div className="game-container">
      <div id="phaser-game" className="game-canvas" />
      {!isReady && (
        <div className="loading-overlay">
          <div className="loading-spinner">Loading...</div>
        </div>
      )}
      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={handleCloseModal}
        onSignIn={handleSignIn}
        onRegisterUsername={handleRegisterUsername}
        isSignedIn={user !== null}
        hasUsername={user?.hasUsername || false}
      />
      <UpgradeModal
        isOpen={isUpgradeModalOpen}
        onClose={() => setIsUpgradeModalOpen(false)}
        user={user}
      />
    </div>
  );
}
